import PageTitle from "../components/subcomponents/PageTitle";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, {useEffect, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {TablePaper} from "../components/TablePaper";
import {useTranslation} from "react-i18next";
import {BusinessAuditLog, ErrorLog} from "sharedlibs";
import {getBusinessAuditLog, getErrorLog} from "../api/auditlog";
import {businessAuditLogTableColumns, errorLogTableColumns} from "../constants";
import {InnerPaperContainer} from "../components/subcomponents/Containers/Containers";
import LMTable from "../components/Tables/LMTable";
import {CellParams, LMColumn, LMTableApi, PaginationModel, SortModel} from "../components/Tables/types";
import TableSearch from "../components/subcomponents/TableSearch";
import {parseFailedInstances, splitMessageOnFailedInstances} from "../utils/errorLogLinks";

type BalPageCache = Map<number, BusinessAuditLog[]>
type ElPageCache = Map<number, ErrorLog[]>

interface DiffEntry {
  field: string
  oldVal: string
  newVal: string
}

function serializeValue(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return ''
  return String(value)
}

function isSkipped(value: unknown): boolean {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Computes the diff between prev and next.
 * - CREATE (prev empty, next exists): returns all fields from next
 * - DELETE (prev exists, next empty): returns all fields from prev
 * - UPDATE (both exist): returns only shared fields that changed
 */
function computeDiff(prevJson: string, newJson: string): DiffEntry[] {
  let prev: Record<string, unknown> | null = null
  let next: Record<string, unknown> | null = null

  try { prev = prevJson ? JSON.parse(prevJson) : null } catch { prev = null }
  try { next = newJson ? JSON.parse(newJson) : null } catch { next = null }

  if (!prev && !next) return []

  // CREATE — no previous value, show all new fields
  if (!prev && next) {
    return Object.entries(next)
      .filter(([, value]) => !isSkipped(value))
      .map(([fieldName, value]) => ({field: fieldName, oldVal: '', newVal: serializeValue(value)}))
  }

  // DELETE — no new value, show all old fields
  if (prev && !next) {
    return Object.entries(prev)
      .filter(([, value]) => !isSkipped(value))
      .map(([fieldName, value]) => ({field: fieldName, oldVal: serializeValue(value), newVal: ''}))
  }

  // UPDATE — only compare shared fields
  const prevKeys = Object.keys(prev ?? {})
  const nextKeys = new Set(Object.keys(next ?? {}))
  const sharedKeys = prevKeys.filter(k => nextKeys.has(k))

  const diffs: DiffEntry[] = []

  for (const key of sharedKeys) {
    const oldVal = prev?.[key]
    const newVal = next?.[key]

    if (isSkipped(oldVal) || isSkipped(newVal)) continue

    const oldStr = serializeValue(oldVal)
    const newStr = serializeValue(newVal)

    if (oldStr !== newStr) {
      diffs.push({field: key, oldVal: oldStr, newVal: newStr})
    }
  }

  return diffs
}

/**
 * Builds a short string showing only the changed fields for one side (old or new).
 * e.g. "name: Muster, zip: 12345"
 */
function formatChangedFields(diffs: DiffEntry[], side: 'old' | 'new'): string {
  if (diffs.length === 0) return '–'
  return diffs
    .filter(d => side === 'old' ? d.oldVal : d.newVal)
    .map(d => `${d.field}: ${side === 'old' ? d.oldVal : d.newVal}`)
    .join(', ') || '–'
}

export default function AuditLogPage() {

  const {t} = useTranslation()
  const navigate = useNavigate()

  const [selectedBALRowIds, setSelectedBALRowIds] = React.useState<string[]>([]);
  const [selectedELRowIds, setSelectedELRowIds] = React.useState<string[]>([]);

  const [loadingBAL, setLoadingBAL] = React.useState(true)
  const [loadingEL, setLoadingEL] = React.useState(true)

  const balApiRef = React.useRef<LMTableApi | null>(null);
  const elApiRef = React.useRef<LMTableApi | null>(null);

  const [balPagination, setBalPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});
  const [elPagination, setElPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});

  const [balSort, setBalSort] = React.useState<SortModel>([{field: "timestamp", sort: "desc"}]);
  const [elSort, setElSort] = React.useState<SortModel>([{field: "timestamp", sort: "desc"}]);

  const [balSearch, setBalSearch] = React.useState('');
  const [elSearch, setElSearch] = React.useState('');
  const [balSearchDebounced, setBalSearchDebounced] = React.useState('');
  const [elSearchDebounced, setElSearchDebounced] = React.useState('');

  useEffect(() => {
    const timer = setTimeout(() => setBalSearchDebounced(balSearch), 400)
    return () => clearTimeout(timer)
  }, [balSearch])

  useEffect(() => {
    const timer = setTimeout(() => setElSearchDebounced(elSearch), 400)
    return () => clearTimeout(timer)
  }, [elSearch])

  const [balTotal, setBalTotal] = React.useState(0);
  const [elTotal, setElTotal] = React.useState(0);

  const balCache = React.useRef<BalPageCache>(new Map());
  const elCache = React.useRef<ElPageCache>(new Map());

  const inflightBal = React.useRef<Map<string, AbortController>>(new Map());
  const inflightEl = React.useRef<Map<string, AbortController>>(new Map());

  // Identifies which query the cached pages belong to. Page number is *not* part
  // of it — pages of the same query share a cache; a different pageSize or search
  // term is a different query and invalidates all of them.
  const balQueryKey = `${balPagination.pageSize}|${balSearchDebounced}`;
  const elQueryKey = `${elPagination.pageSize}|${elSearchDebounced}`;

  const balCachedQuery = React.useRef(balQueryKey);
  const elCachedQuery = React.useRef(elQueryKey);

  // Invalidation happens here, during render, and not in an effect. As an effect
  // it ran *after* the fetch effect below (effects fire in declaration order),
  // so on every mount it aborted the very first-page request that effect had
  // just started — and because `setBalTotal(0)` was a no-op on a fresh mount,
  // nothing re-triggered the fetch. Both tables stayed permanently empty.
  if (balCachedQuery.current !== balQueryKey) {
    balCachedQuery.current = balQueryKey;
    balCache.current.clear();
    inflightBal.current.forEach(ac => ac.abort());
    inflightBal.current.clear();
  }

  if (elCachedQuery.current !== elQueryKey) {
    elCachedQuery.current = elQueryKey;
    elCache.current.clear();
    inflightEl.current.forEach(ac => ac.abort());
    inflightEl.current.clear();
  }

  // ── Changes detail dialog ──
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [dialogDiffs, setDialogDiffs] = React.useState<DiffEntry[]>([])

  const openChangesDialog = React.useCallback((prevValue: string, newValue: string) => {
    setDialogDiffs(computeDiff(prevValue, newValue))
    setDialogOpen(true)
  }, [])

  // ── BAL columns with custom renderCell for prevValue / newValue ──

  const balGridColumns: LMColumn[] = useMemo(() => {
    return businessAuditLogTableColumns.map((col): LMColumn => {
      const base: LMColumn = {
        field: col.field,
        headerName: col.headerName,
        width: col.width ?? 100,
      }

      if (col.field === 'customerId') {
        return {
          ...base,
          renderCell: (params: CellParams) => {
            const customerId = params.value as string
            if (!customerId) return <Typography variant="body2" color="text.secondary">–</Typography>
            return (
              <Button
                size="small"
                variant="text"
                sx={{p: 0, minWidth: 0, textTransform: 'none', fontWeight: 'normal'}}
                onClick={() => navigate(`/customers/update/${customerId}`)}
              >
                {customerId}
              </Button>
            )
          }
        }
      }

      if (col.field === 'message') {
        return {
          ...base,
          renderCell: (params: CellParams) => {
            const key = params.value as string
            const translated = t(key)
            // If t() returns the key itself, show it as-is (legacy or unknown key)
            return <span>{translated}</span>
          }
        }
      }

      if (col.field === 'prevValue') {
        return {
          ...base,
          sortable: false,
          filterable: false,
          renderCell: (params: CellParams) => {
            const row = params.row
            const prev = row._prevValue ?? ''
            const newV = row._newValue ?? ''
            if (!prev) return <Typography variant="body2" color="text.secondary">–</Typography>

            const diffs = computeDiff(prev, newV)
            const label = diffs.length > 0 ? formatChangedFields(diffs, 'old') : '–'

            return (
              <Chip
                label={label}
                size="small"
                color="error"
                variant="outlined"
                onClick={(e) => { e.stopPropagation(); openChangesDialog(prev, newV) }}
                sx={{cursor: 'pointer', maxWidth: '100%'}}
              />
            )
          }
        }
      }

      if (col.field === 'newValue') {
        return {
          ...base,
          sortable: false,
          filterable: false,
          renderCell: (params: CellParams) => {
            const row = params.row
            const prev = row._prevValue ?? ''
            const newV = row._newValue ?? ''
            if (!newV) return <Typography variant="body2" color="text.secondary">–</Typography>

            const diffs = computeDiff(prev, newV)
            const label = diffs.length > 0 ? formatChangedFields(diffs, 'new') : '–'

            return (
              <Chip
                label={label}
                size="small"
                color="success"
                variant="outlined"
                onClick={(e) => { e.stopPropagation(); openChangesDialog(prev, newV) }}
                sx={{cursor: 'pointer', maxWidth: '100%'}}
              />
            )
          }
        }
      }

      return base
    })
  }, [t, openChangesDialog])

  // ── EL columns with custom renderCell for message (links solIds to their product page) ──

  const elGridColumns: LMColumn[] = useMemo(() => {
    return errorLogTableColumns.map((col): LMColumn => {
      const base: LMColumn = {
        field: col.field,
        headerName: col.headerName,
        width: col.width ?? 100,
      }

      if (col.field === 'message') {
        return {
          ...base,
          renderCell: (params: CellParams) => {
            const message = params.value as string
            const metadataRaw = params.row.metadata as string | null | undefined

            // Only entries logged with a { failedInstances: [{ solId, customerId }] } metadata
            // payload (currently: the feature toggle refresh cron) get links — everything else
            // falls through to the plain message text unchanged (parseFailedInstances returns
            // [] for that case, and splitMessageOnFailedInstances then returns the whole
            // message as one plain-text part).
            const failedInstances = parseFailedInstances(metadataRaw)
            const parts = splitMessageOnFailedInstances(message, failedInstances)

            return (
              <span>
                {parts.map((part, i) => {
                  if (part.type === 'text') return <React.Fragment key={i}>{part.text}</React.Fragment>
                  return (
                    <Button
                      key={i}
                      size="small"
                      variant="text"
                      sx={{p: 0, minWidth: 0, textTransform: 'none', fontWeight: 'normal', verticalAlign: 'baseline'}}
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/customers/update/${part.customerId}?product=${part.solId}&fallbackURL=auditlog`)
                      }}
                    >
                      {part.solId}
                    </Button>
                  )
                })}
              </span>
            )
          }
        }
      }

      return base
    })
  }, [navigate])

  // ── Business Audit Log ──

  const fetchBalPage = React.useCallback(async (page: number) => {
    if (page < 0) return
    if (balCache.current.has(page)) return

    const queryKey = `${balPagination.pageSize}|${balSearchDebounced}`;
    const key = `bal|${page}|${queryKey}`;
    if (inflightBal.current.has(key)) return;

    const ac = new AbortController()
    inflightBal.current.set(key, ac);
    try {
      const res = await getBusinessAuditLog({
        page: page + 1,
        pageSize: balPagination.pageSize,
        search: balSearchDebounced || undefined,
      }, {signal: ac.signal})

      // A response that outran its abort belongs to a query we no longer show.
      if (balCachedQuery.current !== queryKey) return;

      const newTotal = res?.pageInfo?.total ?? 0;
      setBalTotal(prev => (prev !== newTotal ? newTotal : prev));

      const mapped: BusinessAuditLog[] = (res.items ?? []).map(i => {
        const d = new Date(i.timestamp)
        const pad = (n: number) => String(n).padStart(2, '0');
        const ts = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${pad(d.getFullYear())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`

        return {...i, timestamp: ts, _prevValue: i.prevValue ?? '', _newValue: i.newValue ?? ''}
      })

      balCache.current.set(page, mapped)
    } catch (e: any) {
      if (e?.name !== 'AbortError') console.error('fetchBalPage failed', e);
    } finally {
      inflightBal.current.delete(key);
    }
  }, [balPagination.pageSize, balSearchDebounced])

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoadingBAL(true)
      const current = balPagination.page
      const pageSize = balPagination.pageSize

      await fetchBalPage(current)

      const lastPage = Math.max(0, Math.ceil(balTotal / pageSize) - 1)
      const prev = current - 1
      const next = current + 1

      // Only prefetch `next` once we know a total. The old `balTotal === 0 ||` escape
      // hatch fired on every first mount (balTotal starts at 0), requesting a page
      // that often doesn't exist and caching the empty response under that index —
      // where fetchBalPage's cache check would then keep serving it. Nothing is lost:
      // fetchBalPage(current) sets balTotal, which re-runs this effect.
      await Promise.all([
        prev >= 0 ? fetchBalPage(prev) : Promise.resolve(),
        balTotal > 0 && next <= lastPage ? fetchBalPage(next) : Promise.resolve()
      ])

      if (mounted) setLoadingBAL(false)
    })()

    return () => {
      mounted = false
    }
  }, [balPagination.page, balPagination.pageSize, balTotal, fetchBalPage])

  // Cache invalidation for a new query is handled during render (see above); all
  // that is left here is jumping back to the first page. Deliberately no abort
  // and no cache clearing, so this cannot race the fetch effect either way.
  useEffect(() => {
    setBalPagination(prev => prev.page === 0 ? prev : {...prev, page: 0})
  }, [balPagination.pageSize, balSearchDebounced])

  const balRows = balCache.current.get(balPagination.page) ?? []

  // ── Error Log ──

  const fetchElPage = React.useCallback(async (page: number) => {
    if (page < 0) return
    if (elCache.current.has(page)) return

    const queryKey = `${elPagination.pageSize}|${elSearchDebounced}`;
    const key = `el|${page}|${queryKey}`;
    if (inflightEl.current.has(key)) return;

    const ac = new AbortController()
    inflightEl.current.set(key, ac);
    try {
      const res = await getErrorLog({
        page: page + 1,
        pageSize: elPagination.pageSize,
        search: elSearchDebounced || undefined,
      }, {signal: ac.signal})

      // See the business-log fetch: drop a response whose query has moved on.
      if (elCachedQuery.current !== queryKey) return;

      const newTotal = res?.pageInfo?.total ?? 0;
      setElTotal(prev => (prev !== newTotal ? newTotal : prev));

      const mapped: ErrorLog[] = (res.items ?? []).map(i => {
        const d = new Date(i.timestamp)
        const pad = (n: number) => String(n).padStart(2, '0');
        const ts = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${pad(d.getFullYear())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`

        return {...i, timestamp: ts}
      })

      elCache.current.set(page, mapped)
    } catch (e: any) {
      if (e?.name !== 'AbortError') console.error('fetchElPage failed', e);
    } finally {
      inflightEl.current.delete(key);
    }
  }, [elPagination.pageSize, elSearchDebounced])

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoadingEL(true)
      const current = elPagination.page
      const pageSize = elPagination.pageSize

      await fetchElPage(current)

      const lastPage = Math.max(0, Math.ceil(elTotal / pageSize) - 1)
      const prev = current - 1
      const next = current + 1

      // Same as the business log above: no speculative prefetch before elTotal lands.
      await Promise.all([
        prev >= 0 ? fetchElPage(prev) : Promise.resolve(),
        elTotal > 0 && next <= lastPage ? fetchElPage(next) : Promise.resolve()
      ])

      if (mounted) setLoadingEL(false)
    })()

    return () => {
      mounted = false
    }
  }, [elPagination.page, elPagination.pageSize, elTotal, fetchElPage])

  // Same as the business log: page reset only, invalidation happens during render.
  useEffect(() => {
    setElPagination(prev => prev.page === 0 ? prev : {...prev, page: 0})
  }, [elPagination.pageSize, elSearchDebounced])

  const elRows = elCache.current.get(elPagination.page) ?? []

  // ── Selection handlers ──

  function handleBALSelectionChange(selectionModel: any) {
    if (selectionModel.length > 1) {
      setSelectedBALRowIds(selectionModel)
    } else {
      setSelectedBALRowIds(selectionModel.splice(-1))
    }
  }

  function handleELSelectionChange(selectionModel: any) {
    if (selectionModel.length > 1) {
      setSelectedELRowIds(selectionModel)
    } else {
      setSelectedELRowIds(selectionModel.splice(-1))
    }
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('pages.auditLogPage.title')}</PageTitle>
      <Divider/>

      <TablePaper>
        <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column'}}>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between'}}>

            <InnerPaperContainer>
              <Typography variant="h4">{t('pages.businessauditlog.title')}</Typography>
            </InnerPaperContainer>
            <Divider/>

            <TableSearch onSearch={setBalSearch}/>

            {loadingBAL ? <Skeleton width={'100%'} height={600}/> : (
              <LMTable
                onSortModelChange={(model) => setBalSort(model)}
                rows={balRows}
                columns={balGridColumns}
                options={{sortModel: balSort}}
                selectionModel={selectedBALRowIds}
                selectionHandler={handleBALSelectionChange}
                apiRef={balApiRef}
                paginationModel={balPagination}
                onPaginationModelChange={setBalPagination}
                rowCount={balTotal}
                loading={loadingBAL}
                showFullContent={false}
              />
            )}

            <div style={{margin: '20px'}}/>

            <InnerPaperContainer>
              <Typography variant="h4">{t('pages.errorlog.title')}</Typography>
            </InnerPaperContainer>

            <TableSearch onSearch={setElSearch}/>

            {loadingEL ? <Skeleton width={'100%'} height={600}/> : (
              <LMTable
                onSortModelChange={(model) => setElSort(model)}
                rows={elRows}
                columns={elGridColumns}
                options={{sortModel: elSort}}
                selectionModel={selectedELRowIds}
                selectionHandler={handleELSelectionChange}
                apiRef={elApiRef}
                paginationModel={elPagination}
                onPaginationModelChange={setElPagination}
                rowCount={elTotal}
                loading={loadingEL}
                showFullContent={false}
              />
            )}
          </Box>
        </Box>
      </TablePaper>

      {/* Changes detail dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('pages.auditLogPage.changesDialog.title')}</DialogTitle>
        <DialogContent dividers>
          {dialogDiffs.length === 0 ? (
            <Typography color="text.secondary">
              {t('pages.auditLogPage.changesDialog.noChanges')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 'bold'}}>{t('pages.auditLogPage.changesDialog.field')}</TableCell>
                    <TableCell sx={{fontWeight: 'bold'}}>{t('pages.auditLogPage.changesDialog.oldValue')}</TableCell>
                    <TableCell sx={{fontWeight: 'bold'}}>{t('pages.auditLogPage.changesDialog.newValue')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogDiffs.map((diff) => (
                    <TableRow key={diff.field}>
                      <TableCell sx={{fontWeight: 500}}>{diff.field}</TableCell>
                      <TableCell
                        sx={{
                          color: diff.oldVal ? 'error.main' : 'text.secondary',
                          userSelect: 'text',
                          wordBreak: 'break-word',
                          maxWidth: 300
                        }}
                      >
                        {diff.oldVal || '–'}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: diff.newVal ? 'success.main' : 'text.secondary',
                          userSelect: 'text',
                          wordBreak: 'break-word',
                          maxWidth: 300
                        }}
                      >
                        {diff.newVal || '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t('pages.auditLogPage.changesDialog.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
